import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Grid,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import ResizeImage from 'tiptap-extension-resize-image';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Image as ImageIcon,
  InsertLink,
  Undo,
  Redo,
  ArrowBack
} from '@mui/icons-material';
import i18next from '../../../lang';
import axios from 'axios';
import {rootStore} from "../../../stores/RootStore";
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { format, parse, isValid, addDays } from 'date-fns';

interface Announcement {
  id: number;
  userId: string;
  title: string;
  content: string;
  isImportant: number;
  isHidden: number;
  createdAt: string;
  updatedAt: string;
}

const AnnouncementEditForm = observer(() => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { viewStatusStore } = rootStore;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isImportant: false,
    isHidden: false,
    usePublishStart: false,
    usePublishEnd: false,
    publishStart: '',
    publishEnd: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');

  // 색상 옵션
  const colorOptions = [
    { label: i18next.t('announcement.black'), value: '#000000' },
    { label: i18next.t('announcement.red'), value: '#FF0000' },
    { label: i18next.t('announcement.blue'), value: '#0000FF' },
    { label: i18next.t('announcement.green'), value: '#008000' },
    { label: i18next.t('announcement.purple'), value: '#800080' },
    { label: i18next.t('announcement.orange'), value: '#FFA500' },
    { label: i18next.t('announcement.gray'), value: '#808080' },
    { label: i18next.t('announcement.brown'), value: '#A52A2A' }
  ];

  // TipTap 에디터 설정
  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizeImage,
      Image.configure({
        HTMLAttributes: {
          class: 'announcement-image',
        },
        allowBase64: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      })
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        content: editor.getHTML()
      }));
    },
    onFocus: () => {
      setIsEditorFocused(true);
    },
    onBlur: () => {
      setIsEditorFocused(false);
    }
  });

  useEffect(() => {
    fetchAnnouncement();
  }, []);

  useEffect(() => {
    if (editor && announcement) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = announcement.content;

      editor.commands.setContent(tempDiv.innerHTML);
    }
  }, [editor, announcement]);

  const fetchAnnouncement = async () => {
    try {
      const response = await axios.get(`/api/v1/admin/announcements/${id}`);
      const data = response.data;
      setAnnouncement(data);
      setFormData({
        title: data.title,
        content: data.content,
        isImportant: data.isImportant === 1,
        isHidden: data.isHidden === 1,
        usePublishStart: data.publishStart !== null,
        usePublishEnd: data.publishEnd !== null,
        publishStart: data.publishStart ? data.publishStart.substring(0, 16) : '',
        publishEnd: data.publishEnd ? data.publishEnd.substring(0, 16) : '',
      });
    } catch (error: any) {
      console.error('Failed to fetch announcement:', error);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    editor?.chain().focus().setColor(color).run();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      viewStatusStore.setLoadingStatus({ open: true });

      await axios.put(`/api/v1/admin/announcements/${id}`, {
        title: formData.title.trim(),
        content: formData.content,
        isImportant: formData.isImportant,
        isHidden: formData.isHidden,
        publishStart: formData.usePublishStart ? formData.publishStart : null,
        publishEnd: formData.usePublishEnd ? formData.publishEnd : null,
      });

      navigate(-1);
    } catch (error: any) {
      console.error('Failed to update announcement:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  }

  const handleCancel = () => {
    navigate(-1);
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }

    event.target.value = '';
  };

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    setError('');

    // 파일 크기 검사 (5MB 제한)
    if (file.size > 5 * 1024 * 1024) {
      setError(i18next.t('announcement.image_size_limit'));
      setUploadingImage(false);
      return;
    }

    // 파일 타입 검사
    if (!file.type.startsWith('image/')) {
      setError(i18next.t('announcement.image_only'));
      setUploadingImage(false);
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        
        // TipTap 에디터에 이미지 삽입
        if (editor) {
          editor.chain().focus().setImage({ 
            src: imageUrl,
            alt: file.name,
            title: file.name
          }).run();
        } else {
          console.error('Editor is not available');
        }
        setUploadingImage(false);
      };
      reader.onerror = () => {
        console.error('FileReader error occurred');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      console.error('Failed to process image:', err);
      setUploadingImage(false);
    }
  };

  if (!editor || !announcement) {
    return null;
  }

  return (
    <Container maxWidth={false} sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1.5, md: 4 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {i18next.t('announcement.edit_announcement')}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* 제목 입력란 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={i18next.t('common.title')}
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                InputProps={{ style: { fontSize: '0.9rem' } }}
                InputLabelProps={{ style: { fontSize: '0.9rem' } }}
              />
            </Grid>

            {/* 중요 공지사항 표시 버튼 */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isImportant}
                    onChange={(e) => handleChange('isImportant', e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                    {i18next.t('announcement.important_notice')}
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isHidden}
                    onChange={(e) => handleChange('isHidden', e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                    {i18next.t('invoice.hidden')}
                  </Typography>
                }
                sx={{ ml: 2 }}
              />
            </Grid>

            {/* 表示期間設定 */}
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
              <Paper variant="outlined" sx={{ p: 1.5, px: 2, bgcolor: '#f8f9fa', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.usePublishStart}
                          onChange={(e) => {
                            handleChange('usePublishStart', e.target.checked);
                            if (!e.target.checked) handleChange('publishStart', '');
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {i18next.t('announcement.publish_start')}
                        </Typography>
                      }
                      sx={{ mr: 0.5 }}
                    />
                    {formData.usePublishStart ? (
                      <DateTimePicker
                        value={formData.publishStart ? parse(formData.publishStart, "yyyy-MM-dd'T'HH:mm", new Date()) : null}
                        onChange={(newValue: Date | null) => {
                          handleChange('publishStart', newValue && isValid(newValue) ? format(newValue, "yyyy-MM-dd'T'HH:mm") : '');
                        }}
                        format="yyyy/MM/dd HH:mm"
                        ampm={false}
                        slotProps={{
                          textField: { size: 'small', sx: { '& .MuiInputBase-input': { fontSize: '0.8rem', width: 130 } } }
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>
                        {i18next.t('announcement.not_set')}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.85rem', color: '#999', mx: 0.5 }}>～</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.usePublishEnd}
                          onChange={(e) => {
                            handleChange('usePublishEnd', e.target.checked);
                            if (!e.target.checked) {
                              handleChange('publishEnd', '');
                            } else if (formData.usePublishStart && formData.publishStart) {
                              const startDate = parse(formData.publishStart, "yyyy-MM-dd'T'HH:mm", new Date());
                              if (isValid(startDate)) {
                                handleChange('publishEnd', format(addDays(startDate, 1), "yyyy-MM-dd'T'HH:mm"));
                              }
                            }
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {i18next.t('announcement.publish_end')}
                        </Typography>
                      }
                      sx={{ mr: 0.5 }}
                    />
                    {formData.usePublishEnd ? (
                      <DateTimePicker
                        value={formData.publishEnd ? parse(formData.publishEnd, "yyyy-MM-dd'T'HH:mm", new Date()) : null}
                        onChange={(newValue: Date | null) => {
                          handleChange('publishEnd', newValue && isValid(newValue) ? format(newValue, "yyyy-MM-dd'T'HH:mm") : '');
                        }}
                        format="yyyy/MM/dd HH:mm"
                        ampm={false}
                        minDateTime={formData.usePublishStart && formData.publishStart ? parse(formData.publishStart, "yyyy-MM-dd'T'HH:mm", new Date()) : undefined}
                        slotProps={{
                          textField: { size: 'small', sx: { '& .MuiInputBase-input': { fontSize: '0.8rem', width: 130 } } }
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>
                        {i18next.t('announcement.not_set')}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {formData.usePublishStart && formData.usePublishEnd &&
                 formData.publishStart && formData.publishEnd &&
                 formData.publishEnd < formData.publishStart && (
                  <Alert severity="error" sx={{ mt: 0.5 }}>
                    {i18next.t('announcement.schedule_error_end_before_start')}
                  </Alert>
                )}
              </Paper>
              </LocalizationProvider>
            </Grid>

            {/* 내용 입력란 (TipTap 에디터) */}
            <Grid item xs={12}>
              {/* 에디터 툴바 */}
              <Paper sx={{ p: 1, mb: 1, bgcolor: '#f8f9fa' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {/* 텍스트 스타일 */}
                  <ToggleButtonGroup size="small" value="" exclusive>
                    <ToggleButton
                      value="bold"
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      selected={editor.isActive('bold')}
                    >
                      <FormatBold fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="italic"
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      selected={editor.isActive('italic')}
                    >
                      <FormatItalic fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="underline"
                      onClick={() => editor.chain().focus().toggleUnderline().run()}
                      selected={editor.isActive('underline')}
                    >
                      <FormatUnderlined fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Divider orientation="vertical" flexItem />

                  {/* 목록 */}
                  <ToggleButtonGroup size="small" value="" exclusive>
                    <ToggleButton
                      value="bullet"
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      selected={editor.isActive('bulletList')}
                    >
                      <FormatListBulleted fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="ordered"
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      selected={editor.isActive('orderedList')}
                    >
                      <FormatListNumbered fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Divider orientation="vertical" flexItem />

                  {/* 정렬 */}
                  <ToggleButtonGroup size="small" value="" exclusive>
                    <ToggleButton
                      value="left"
                      onClick={() => editor.chain().focus().setTextAlign('left').run()}
                      selected={editor.isActive({ textAlign: 'left' })}
                    >
                      <FormatAlignLeft fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="center"
                      onClick={() => editor.chain().focus().setTextAlign('center').run()}
                      selected={editor.isActive({ textAlign: 'center' })}
                    >
                      <FormatAlignCenter fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="right"
                      onClick={() => editor.chain().focus().setTextAlign('right').run()}
                      selected={editor.isActive({ textAlign: 'right' })}
                    >
                      <FormatAlignRight fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="justify"
                      onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                      selected={editor.isActive({ textAlign: 'justify' })}
                    >
                      <FormatAlignJustify fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Divider orientation="vertical" flexItem />

                  {/* 색상 선택 */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <InputLabel sx={{ fontSize: '0.7rem' }}>{i18next.t('common.color')}</InputLabel>
                      <Select
                        value={selectedColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        label={i18next.t('common.color')}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        {colorOptions.map((color) => (
                          <MenuItem key={color.value} value={color.value} sx={{ fontSize: '0.8rem' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  backgroundColor: color.value,
                                  border: '1px solid #ddd',
                                  borderRadius: 1
                                }}
                              />
                              {color.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Divider orientation="vertical" flexItem />

                  {/* 이미지 */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleImageButtonClick}
                      disabled={uploadingImage}
                      startIcon={<ImageIcon />}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {uploadingImage ? i18next.t('common.uploading') : i18next.t('common.image')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const url = window.prompt('URL');
                        if (url) {
                          editor.chain().focus().setLink({ href: url }).run();
                        }
                      }}
                      startIcon={<InsertLink />}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {i18next.t('common.link')}
                    </Button>
                  </Box>

                  <Divider orientation="vertical" flexItem />

                  {/* 실행 취소/다시 실행 */}
                  <ToggleButtonGroup size="small" value="" exclusive>
                    <ToggleButton
                      value="undo"
                      onClick={() => editor.chain().focus().undo().run()}
                      disabled={!editor.can().undo()}
                    >
                      <Undo fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="redo"
                      onClick={() => editor.chain().focus().redo().run()}
                      disabled={!editor.can().redo()}
                    >
                      <Redo fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Paper>

              {/* 에디터 영역 */}
              <Box sx={{ 
                border: '1px solid #ddd', 
                borderRadius: 1,
                minHeight: '300px',
                p: 2,
                bgcolor: '#fff',
                '& .ProseMirror': {
                  minHeight: '280px',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  '& img': {
                    // 기존 이미지의 크기 정보를 존중하되, 최대 너비 제한
                    maxWidth: '100% !important',
                    height: 'auto !important',
                    display: 'block !important',
                    margin: '0.5em 0 !important',
                    borderRadius: '4px !important',
                    visibility: 'visible !important',
                    opacity: '1 !important'
                  },
                  '& .announcement-image': {
                    // 기존 이미지의 크기 정보를 존중하되, 최대 너비 제한
                    maxWidth: '100% !important',
                    height: 'auto !important',
                    display: 'block !important',
                    margin: '0.5em 0 !important',
                    borderRadius: '4px !important',
                    visibility: 'visible !important',
                    opacity: '1 !important'
                  },
                  '& *[src]': {
                    // 기존 이미지의 크기 정보를 존중하되, 최대 너비 제한
                    maxWidth: '100% !important',
                    height: 'auto !important',
                    display: 'block !important',
                    visibility: 'visible !important',
                    opacity: '1 !important'
                  }
                }
              }}
              onClick={() => {
                // 에디터에 포커스가 없을 때만 맨 마지막으로 커서 이동
                if (!isEditorFocused) {
                  editor.chain().focus().run();
                  // 커서를 맨 마지막으로 이동
                  const { state } = editor;
                  const { doc } = state;
                  const end = doc.content.size;
                  editor.chain().focus().setTextSelection(end).run();
                }
              }}
              >
                <EditorContent 
                  editor={editor} 
                  style={{ 
                    minHeight: '280px',
                    fontSize: '0.9rem',
                    lineHeight: 1.6
                  }}
                />
              </Box>
            </Grid>

            {/* 버튼 영역 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ fontSize: '0.9rem' }}
                >
                  {i18next.t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || (formData.usePublishStart && formData.usePublishEnd && formData.publishStart !== '' && formData.publishEnd !== '' && formData.publishEnd < formData.publishStart)}
                  sx={{ fontSize: '0.9rem' }}
                >
                  {loading ? i18next.t('common.registering') : i18next.t('common.finalize')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* 에러 메시지 */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* 성공 메시지 */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
});

export default AnnouncementEditForm; 